import * as React from 'react'

import { MobXProviderContext } from 'mobx-react'

import { FormListSelector } from '~/client/src/shared/components/FormListSelector'
import { useResolvedTemplate } from '~/client/src/shared/components/FormListSelector/useResolvedTemplate'
import { FormPropertiesSidebar } from '~/client/src/shared/components/FormPropertiesSidebar'
import { MaterialResourceEditBody } from '~/client/src/shared/components/MaterialResourceEdit/MaterialResourceEditBody'
import { ResourceEdit } from '~/client/src/shared/components/ResourceViewEdit'
import { SidePanel } from '~/client/src/shared/components/SidePanel/SidePanel'
import Spinner from '~/client/src/shared/components/Spinner/Spinner'
import { getSelectFormTitle } from '~/client/src/shared/constants/resourceTypeLabels'
import { extractControlFields } from '~/client/src/shared/hooks/useResourceControls'
import UserProject from '~/client/src/shared/models/UserProject'

import { useResource } from '../context'
import { useResourceDeleteAction } from '../useResourceDeleteAction'
import { PendingRecordPanel } from './PendingRecordPanel'

interface IProps {
  /** Which section opens expanded by default (e.g. 'controls' from the controls table). */
  defaultExpandedSectionId?: string
}

export const ResourceEditPanel: React.FC<IProps> = ({
  defaultExpandedSectionId,
}) => {
  const {
    state: { selectedRecordId, chosenTemplateId, records },
    actions: { closeEditPanel, save, setChosenTemplateId },
    meta: {
      resourceTypeId,
      runtimeContext,
      projectId,
      schema,
      controlCommands,
    },
  } = useResource()

  const [isCreatingNewForm, setIsCreatingNewForm] = React.useState(false)

  const stores = React.useContext(MobXProviderContext) as any
  const isAdmin = UserProject.isAdmin(
    stores.state?.user,
    stores.userProjectsStore,
  )

  const isCreating = selectedRecordId === 'new'

  const {
    effectiveTemplateId,
    templates,
    loading: templatesLoading,
  } = useResolvedTemplate({
    enabled: isCreating,
    chosenTemplateId,
    templates: schema.templates,
    loading: schema.loading,
  })

  const selectedRecord = React.useMemo(() => {
    if (!selectedRecordId || selectedRecordId === 'new') return null
    return records.find(r => r.id === selectedRecordId) || null
  }, [selectedRecordId, records])

  const editableRecord = React.useMemo(() => {
    if (!selectedRecord) return null
    return {
      ...selectedRecord,
      code: selectedRecord.code ?? null,
      stxResourceId: selectedRecord.stxResourceId ?? null,
      isDeleted: selectedRecord.isDeleted ?? false,
      templateId: selectedRecord.templateId ?? null,
    }
  }, [selectedRecord])

  const qrCodeUrl = React.useMemo(() => {
    if (!selectedRecordId || selectedRecordId === 'new') return null
    return `${window.location.origin}/resource/${resourceTypeId}/${selectedRecordId}`
  }, [selectedRecordId, resourceTypeId])

  const { onDelete, confirmDialog } = useResourceDeleteAction(selectedRecordId)

  if (!selectedRecordId) {
    if (isCreatingNewForm) setIsCreatingNewForm(false)
    return null
  }

  if (isCreating && templatesLoading) {
    return (
      <SidePanel>
        <div className="tw:flex tw:items-center tw:justify-center tw:size-full tw:bg-background">
          <Spinner size="md" />
        </div>
      </SidePanel>
    )
  }

  if (isCreating && !effectiveTemplateId) {
    if (isCreatingNewForm) {
      return (
        <SidePanel>
          <FormPropertiesSidebar
            mode="create"
            resourceTypeId={resourceTypeId}
            onClose={closeEditPanel}
            onBackToPicker={() => setIsCreatingNewForm(false)}
            resourceSchema={schema}
            onCreated={newTemplateId => {
              setIsCreatingNewForm(false)
              setChosenTemplateId(newTemplateId)
            }}
          />
        </SidePanel>
      )
    }
    return (
      <SidePanel>
        <FormListSelector
          title={getSelectFormTitle(resourceTypeId)}
          titleVariant="panel"
          resourceTypeId={resourceTypeId}
          onSelect={setChosenTemplateId}
          onClose={closeEditPanel}
          onCreateNewForm={
            isAdmin ? () => setIsCreatingNewForm(true) : undefined
          }
          templates={templates}
          loading={templatesLoading}
        />
      </SidePanel>
    )
  }

  if (!isCreating && !selectedRecord) return <PendingRecordPanel />

  return (
    <>
      <SidePanel>
        <MaterialResourceEditBody
          recordId={selectedRecordId}
          record={editableRecord}
          templateId={effectiveTemplateId || undefined}
          resourceTypeId={resourceTypeId}
          runtimeContext={runtimeContext}
          projectId={projectId}
          qrCodeUrl={qrCodeUrl}
          isCreationMode={isCreating}
          controlFields={extractControlFields(selectedRecord?.root ?? {})}
          controlCommands={controlCommands}
          onClose={closeEditPanel}
          onSave={save}
          keepOpenOnCreate={true}
          onTemplateChange={setChosenTemplateId}
          onDelete={onDelete}
          defaultExpandedSectionId={defaultExpandedSectionId}
          formSelector={
            <ResourceEdit.FormSelector
              resourceTypeId={resourceTypeId}
              templates={templates}
              currentTemplateId={effectiveTemplateId}
            />
          }
          resourceSchema={schema}
        />
      </SidePanel>
      {confirmDialog}
    </>
  )
}
